// =============================================================================
// Tiện ích thuần của module Hạ COD (nhãn, màu, 2 ô tiền liên kết, kiểm form).
// CHỈ GHI NHẬN: không có phép tính nào ở đây đụng tới tiền của đơn.
// =============================================================================
import type {
  CodReductionActionKind,
  CodReductionAttachmentKind,
  CodReductionStatus,
  CodReductionStepStatus,
  CodReductionStepType,
  ShipmentStatus,
} from '@/types/db'
import { carrierName } from '@/features/orders/shipping-options'
import { PERMISSIONS } from '@/lib/permissions'

export { fmtVnd, fmtDateTimeVi } from '@/features/expenses/expense-utils'

// Có 1 trong các quyền này → menu "Hạ COD" hiện thường trực. Người duyệt theo người / vị
// trí / sale phụ trách KHÔNG cần quyền nào: menu hiện khi có đề nghị chờ họ (badge) và
// route danh sách + chi tiết không gate quyền (RLS chỉ trả đề nghị họ được xem).
export const COD_REDUCTION_MODULE_PERMISSIONS = [
  PERMISSIONS.COD_REDUCTION_REQUEST,
  PERMISSIONS.COD_REDUCTION_VIEW,
  PERMISSIONS.COD_REDUCTION_APPROVE,
  PERMISSIONS.COD_REDUCTION_FLOW_MANAGE,
  PERMISSIONS.COD_REDUCTION_REASON_MANAGE,
  PERMISSIONS.COD_REDUCTION_REPORT_VIEW,
] as const

// Khớp hàm DB cod_reduction_status_vi — sửa thì sửa CẢ 2.
export const COD_REDUCTION_STATUS_LABEL: Record<CodReductionStatus, string> = {
  pending: 'Đang duyệt',
  approved: 'Chờ hạ COD',
  completed: 'Đã hạ COD',
  rejected: 'Bị từ chối',
  cancelled: 'Đã huỷ',
}

// Cùng bảng màu badge với Duyệt chi (EXPENSE_STATUS_TONE).
export const COD_REDUCTION_STATUS_TONE: Record<CodReductionStatus, string> = {
  pending: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  approved: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  completed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  rejected: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
}

// Thứ tự chip trạng thái ở danh sách.
export const COD_REDUCTION_STATUSES = [
  'pending',
  'approved',
  'completed',
  'rejected',
  'cancelled',
] as const satisfies ReadonlyArray<CodReductionStatus>

export const COD_REDUCTION_STEP_STATUS_LABEL: Record<CodReductionStepStatus, string> = {
  pending: 'Đang chờ',
  approved: 'Đã duyệt',
  auto_approved: 'Tự duyệt',
  rejected: 'Từ chối',
  skipped: 'Bỏ qua',
}

export const COD_REDUCTION_STEP_TYPE_LABEL: Record<CodReductionStepType, string> = {
  order_sale: 'Sale phụ trách đơn',
  sale_superior: 'Cấp trên của Sale phụ trách',
  requester: 'Người tạo đề nghị',
  permission: 'Theo quyền',
  position: 'Theo vị trí',
  user: 'Người đích danh',
}

export const COD_REDUCTION_ACTION_LABEL: Record<CodReductionActionKind, string> = {
  approve: 'Duyệt',
  confirm: 'Xác nhận đã hạ COD',
}

// Bước duyệt "Theo quyền" ở Cấu hình luồng — DB chỉ nhận quyền cod_reduction:* (CHECK).
export const COD_REDUCTION_PERMISSION_OPTIONS: { value: string; label: string }[] = [
  { value: 'cod_reduction:approve', label: 'Người có quyền "Duyệt đề nghị hạ COD"' },
]

// Loại "ai làm" chọn được cho bước DUYỆT. Bước xác nhận cố định = người tạo đề nghị.
export const COD_REDUCTION_APPROVE_STEP_TYPES = [
  'order_sale',
  'sale_superior',
  'permission',
  'position',
  'user',
] as const satisfies ReadonlyArray<CodReductionStepType>

export function codReductionPermissionLabel(perm: string | null): string {
  return COD_REDUCTION_PERMISSION_OPTIONS.find((o) => o.value === perm)?.label ?? perm ?? ''
}

export const ATTACHMENT_KIND_LABEL: Record<CodReductionAttachmentKind, string> = {
  request_doc: 'Chứng từ đề nghị',
  confirm_doc: 'Chứng từ đã hạ COD',
}
// Nhãn chèn vào tên file trên Drive (<mã>_<nhãn>_<tên gốc>.<đuôi>).
export const ATTACHMENT_FILE_LABEL: Record<CodReductionAttachmentKind, string> = {
  request_doc: 'chung-tu',
  confirm_doc: 'xac-nhan-ha-cod',
}

// Mã gốc của chứng từ; file thứ n mang mã `<gốc>-<n>` (HC-12-1, HC-12-XN-1).
export function attachmentBaseCode(code: string, kind: CodReductionAttachmentKind): string {
  return kind === 'confirm_doc' ? `${code}-XN` : code
}

const SHIPMENT_STATUS_VI: Record<ShipmentStatus, string> = {
  created: 'Đã tạo vận đơn',
  picked_up: 'Đã lấy hàng',
  in_transit: 'Đang giao',
  delivered: 'Đã giao',
  failed: 'Giao không thành',
  returned: 'Đã hoàn',
  cancelled: 'Đã huỷ vận đơn',
}
// Ưu tiên chữ hãng trả về (đối chiếu 1-1 với trang hãng), không có thì nhãn chuẩn hoá.
export function shipmentStatusText(status: ShipmentStatus | null, statusText: string | null): string {
  const t = statusText?.trim()
  if (t) return t
  return status ? SHIPMENT_STATUS_VI[status] : '—'
}

export function providerLabel(provider: string | null, providerName: string | null): string {
  return providerName?.trim() || carrierName(provider) || '—'
}

// Tỉ lệ hạ (%) — 1 chữ số lẻ. COD ban đầu ≤ 0 → 0.
export function reducePercent(original: number, reduce: number): number {
  if (!(original > 0)) return 0
  return Math.round((reduce / original) * 1000) / 10
}

// --- 2 ô tiền liên kết: "Số tiền hạ" ⇄ "COD sau hạ" (cộng lại = COD hiện tại) ---------
export type AmountField = 'reduce' | 'newCod'
export type LinkedAmounts = { reduce: number | null; newCod: number | null }

// Gõ ô nào thì tính ô còn lại; kẹp trong [0, COD hiện tại]. Xoá trắng → cả 2 trống.
export function linkAmounts(original: number | null, field: AmountField, value: number | null): LinkedAmounts {
  if (value == null || original == null || !(original > 0)) {
    return field === 'reduce' ? { reduce: value, newCod: null } : { reduce: null, newCod: value }
  }
  const v = Math.min(Math.max(Math.round(value), 0), original)
  return field === 'reduce' ? { reduce: v, newCod: original - v } : { reduce: original - v, newCod: v }
}

// Lỗi chặn gửi (rỗng = gửi được). Cùng luật với RPC create/resubmit_cod_reduction.
export function validateReduction(input: {
  original: number | null
  newCod: number | null
  reason: { requires_note: boolean } | null
  note: string
}): string[] {
  const errs: string[] = []
  if (input.original == null || !(input.original > 0)) errs.push('Đơn chưa có COD để hạ')
  if (input.newCod == null) errs.push('Nhập số tiền hạ hoặc COD sau hạ')
  else if (input.newCod < 0) errs.push('COD sau hạ không được âm')
  else if (input.original != null && input.newCod >= input.original) {
    errs.push('Số tiền hạ phải lớn hơn 0 (COD sau hạ nhỏ hơn COD hiện tại)')
  }
  if (!input.reason) errs.push('Chọn lý do hạ COD')
  else if (input.reason.requires_note && !input.note.trim()) errs.push('Lý do này cần ghi chú cụ thể')
  return errs
}

// Bước của đề nghị đã đóng (từ chối / huỷ / xong) mà chưa tới lượt → không còn "đang chờ".
export function stepStatusText(stepStatus: CodReductionStepStatus, requestStatus: CodReductionStatus): string {
  if (stepStatus === 'pending' && requestStatus !== 'pending' && requestStatus !== 'approved') {
    return 'Không tới lượt'
  }
  return COD_REDUCTION_STEP_STATUS_LABEL[stepStatus]
}
